import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  Calendar, Webhook, BarChart2, Table2, Key, Code2, ShieldCheck,
  FileJson, GitMerge, Zap, TrendingUp, Settings2
} from "lucide-react";

const FEATURES = [
  {
    title: "Scheduled Runs",
    description: "Run templates automatically on a cron schedule — daily, weekly, hourly.",
    icon: Calendar, color: "#006080", bg: "#e6f2f5",
    path: "/power/schedules", tag: "Very High Impact",
  },
  {
    title: "Webhook Triggers",
    description: "External systems POST to a unique URL to trigger template execution in real-time.",
    icon: Webhook, color: "#7C3AED", bg: "#EDE9FE",
    path: "/power/webhooks", tag: "High Impact",
  },
  {
    title: "Analytics Dashboard",
    description: "Charts showing execution trends, success rates, and API performance over time.",
    icon: BarChart2, color: "#059669", bg: "#D1FAE5",
    path: "/power/analytics", tag: "High Impact",
  },
  {
    title: "Data Profiler",
    description: "Column-level statistics: null %, unique count, min/max/avg, top values per column.",
    icon: Table2, color: "#D97706", bg: "#FEF3C7",
    path: "/power/profiler", tag: "High Impact",
  },
  {
    title: "Variables Store",
    description: "Global key-value constants for base URLs, tokens. Use as {{$VAR:KEY}} anywhere.",
    icon: Key, color: "#DC2626", bg: "#FEE2E2",
    path: "/power/variables", tag: "Medium Impact",
  },
  {
    title: "Custom Functions",
    description: "Save reusable named expression snippets and use them across all pipelines.",
    icon: Code2, color: "#2563EB", bg: "#DBEAFE",
    path: "/power/functions", tag: "Medium Impact",
  },
  {
    title: "Validation Rules",
    description: "Define data quality rules that warn or block execution before any API call.",
    icon: ShieldCheck, color: "#06B6D4", bg: "#CFFAFE",
    path: "/power/validation", tag: "High Impact",
  },
  {
    title: "Import from OpenAPI",
    description: "Paste a Swagger/OpenAPI spec URL to auto-generate API definitions instantly.",
    icon: FileJson, color: "#8B5CF6", bg: "#EDE9FE",
    path: "/power/openapi", tag: "Medium Impact",
  },
  {
    title: "Multi-File Merge",
    description: "Join two uploaded files on a key column (left/inner/outer join) into one dataset.",
    icon: GitMerge, color: "#F59E0B", bg: "#FEF3C7",
    path: "/power/merge", tag: "Medium Impact",
  },
];

export default function PowerSuiteHubPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="p-6 max-w-6xl mx-auto fade-in">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#006080] to-[#7C3AED] flex items-center justify-center">
            <Zap size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Power Suite</h1>
            <p className="text-gray-500 text-sm">Advanced automation, analytics, and developer tools</p>
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-4 mb-8 bg-gradient-to-r from-[#004d66] to-[#006080] rounded-xl p-5 text-white">
        {[
          ["9", "Features available"],
          ["Automated", "End-to-end processing"],
          ["Real-time", "Analytics & monitoring"],
        ].map(([val, label]) => (
          <div key={label} className="text-center">
            <div className="text-2xl font-black">{val}</div>
            <div className="text-white/60 text-xs mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      {/* Feature grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {FEATURES.map(({ title, description, icon: Icon, color, bg, path, tag }) => (
          <button
            key={path}
            className="bg-white border border-gray-200 rounded-xl p-5 text-left hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 group"
            onClick={() => navigate(path)}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: bg }}>
                <Icon size={20} style={{ color }} />
              </div>
              <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ background: bg, color }}>
                {tag}
              </span>
            </div>
            <h3 className="font-bold text-gray-900 text-sm mb-1 group-hover:text-[#006080] transition-colors">{title}</h3>
            <p className="text-xs text-gray-500 leading-relaxed">{description}</p>
          </button>
        ))}
      </div>
    </div>
  );
}
